var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import shopify from "./storeFront";
const productFields = /* GraphQL */ `
	fragment productFields on Product {
		id
		title
		handle
		vendor
		images(first: 2) {
			edges {
				node {
					src
				}
			}
		}
		featuredImage {
			src
		}
		media(first: 100) {
			edges {
				node {
					mediaContentType
					alt
					...mediaFieldsByType
				}
			}
		}
		tags
		selectedOrFirstAvailableVariant {
			id
			sku
			selectedOptions {
				name
				value
			}
			priceV2 {
				amount
				currencyCode
			}
			compareAtPriceV2 {
				amount
				currencyCode
			}
		}
		variants(first: 50) {
			edges {
				node {
					id
					sku
					title
					sku
					availableForSale
					selectedOptions {
						name
						value
					}
					priceV2 {
						amount
						currencyCode
					}
					compareAtPriceV2 {
						amount
						currencyCode
					}
				}
			}
		}
		productType
		metafields(identifiers: [{namespace: "custom", key: "promotion"}]) {
			namespace
			key
			reference {
				... on Metaobject {
					fields {
						key
						value
					}
				}
			}
		}
	}
	fragment mediaFieldsByType on Media {
		...on ExternalVideo {
			id
			embeddedUrl
		}
		...on MediaImage {
			image {
				url
			}
		}
		...on Model3d {
			sources {
				url
				mimeType
				format
				filesize
			}
		}
		...on Video {
			sources {
				url
				mimeType
				format
				height
				width
			}
		}
	}
`;
const getProductInfo = /* GraphQL */ `
	query ProductById($id: ID!) {
		product(id: $id) {
			...productFields
		}
	}
	${productFields}
`;
const getProductInfoBatch = /* GraphQL */ `
	query ProductsByIds($ids: [ID!]!) {
		nodes(ids: $ids) {
			... on Product {
				...productFields
			}
		}
	}
	${productFields}
`;
// models_featured: list of Model metaobjects → model_size. related_products: product refs → title, handle, product_front_media (image metafield).
const getBirdsnestTVData = /* GraphQL */ `
	query BirdsnestTVData($ids: [ID!]!) {
		nodes(ids: $ids) {
			... on Product {
				id
				modelsFeatured: metafield(namespace: "custom", key: "models_featured") {
					references(first: 1) {
						nodes {
							... on Metaobject {
								field(key: "model_size") {
									value
								}
							}
						}
					}
				}
				relatedProducts: metafield(namespace: "custom", key: "related_products") {
					references(first: 10) {
						nodes {
							... on Product {
								id
								title
								handle
								productFrontImage: metafield(namespace: "custom", key: "product_front_media") {
									reference {
										... on MediaImage {
											alt
											image {
												url(transform: { maxWidth: 100 })
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;
// In-memory cache for product data keyed by numeric product ID
const productCache = new Map();
const getProductInfoQuery = (id) => __awaiter(void 0, void 0, void 0, function* () {
    const cached = productCache.get(id);
    if (cached) {
        return { data: cached, errors: null };
    }
    try {
        const { data, errors } = yield shopify.request(getProductInfo, {
            variables: {
                id: `gid://shopify/Product/${id}`,
            },
        });
        if (data === null || data === void 0 ? void 0 : data.product) {
            productCache.set(id, data);
        }
        return { data, errors };
    }
    catch (error) {
        console.error(error);
    }
});
// Batching: collect IDs from all callers, defer until browser is idle, then fire chunked requests
const BATCH_CHUNK_SIZE = 10;
let pendingIds = new Set();
let pendingFlush = null;
const scheduleIdle = typeof requestIdleCallback === 'function'
    ? (cb) => requestIdleCallback(cb)
    : (cb) => setTimeout(cb, 200);
function fetchChunk(ids) {
    return __awaiter(this, void 0, void 0, function* () {
        const gids = ids.map((id) => `gid://shopify/Product/${id}`);
        try {
            const { data } = yield shopify.request(getProductInfoBatch, {
                variables: { ids: gids },
            });
            if (data === null || data === void 0 ? void 0 : data.nodes) {
                for (const node of data.nodes) {
                    if (!(node === null || node === void 0 ? void 0 : node.id))
                        continue;
                    const numericId = node.id.split("/").pop();
                    if (numericId) {
                        productCache.set(numericId, { product: node });
                    }
                }
            }
        }
        catch (error) {
            console.error("[preloadProductInfo] Batch chunk failed:", error);
        }
    });
}
export function preloadProductInfo(ids) {
    for (const id of ids) {
        if (!productCache.has(id))
            pendingIds.add(id);
    }
    if (pendingIds.size === 0)
        return Promise.resolve();
    if (!pendingFlush) {
        pendingFlush = new Promise((resolve) => {
            // Defer until browser is idle so we don't block initial render
            scheduleIdle(() => __awaiter(this, void 0, void 0, function* () {
                const all = [...pendingIds];
                pendingIds = new Set();
                pendingFlush = null;
                // Split into chunks to stay within Shopify query cost limits
                const chunks = [];
                for (let i = 0; i < all.length; i += BATCH_CHUNK_SIZE) {
                    chunks.push(all.slice(i, i + BATCH_CHUNK_SIZE));
                }
                yield Promise.all(chunks.map(fetchChunk));
                resolve();
            }));
        });
    }
    return pendingFlush;
}
/** Chunk size for collection `nodes(ids:)` batching (Shopify allows up to 250 ids per request). */
const STOREFRONT_NODES_MAX_IDS = 10;
const collectionHandleById = new Map();
export function getCachedCollectionHandleForCatid(catid) {
    var _a;
    const collectionId = (_a = String(catid).match(/_(\d+)$/)) === null || _a === void 0 ? void 0 : _a[1];
    return collectionId ? collectionHandleById.get(collectionId) : undefined;
}
const collectionsByIdsQuery = /* GraphQL */ `
	query CollectionsByIds($ids: [ID!]!) {
		nodes(ids: $ids) {
			... on Collection {
				id
				handle
			}
		}
	}
`;
let pendingCollectionIds = new Set();
let pendingCollectionFlush = null;
function fetchCollectionChunk(ids) {
    return __awaiter(this, void 0, void 0, function* () {
        const gids = ids.map((id) => `gid://shopify/Collection/${id}`);
        try {
            const { data } = yield shopify.request(collectionsByIdsQuery, {
                variables: { ids: gids },
            });
            if (data === null || data === void 0 ? void 0 : data.nodes) {
                for (const node of data.nodes) {
                    if (!(node === null || node === void 0 ? void 0 : node.id))
                        continue;
                    const numericId = node.id.split("/").pop();
                    if (numericId && "handle" in node && node.handle) {
                        collectionHandleById.set(numericId, node.handle);
                    }
                }
            }
        }
        catch (error) {
            console.error("[preloadCollectionHandlesFromStorefrontAPI] Batch chunk failed:", error);
        }
    });
}
/** Deferred Storefront fetch: parses Shopify collection id from Fredhopper `catid` (e.g. `catalog01_287252447335`). */
export function preloadCollectionHandlesFromStorefrontAPI(catids) {
    var _a;
    for (const catid of catids) {
        const collectionId = (_a = String(catid).match(/_(\d+)$/)) === null || _a === void 0 ? void 0 : _a[1];
        if (collectionId && !collectionHandleById.has(collectionId))
            pendingCollectionIds.add(collectionId);
    }
    if (pendingCollectionIds.size === 0)
        return Promise.resolve();
    if (!pendingCollectionFlush) {
        pendingCollectionFlush = new Promise((resolve) => {
            scheduleIdle(() => {
                const all = [...pendingCollectionIds];
                pendingCollectionIds = new Set();
                pendingCollectionFlush = null;
                // One lightweight `nodes` query — batch all collection GIDs up to platform limit (not product chunk size 10).
                const chunks = [];
                for (let i = 0; i < all.length; i += STOREFRONT_NODES_MAX_IDS) {
                    chunks.push(all.slice(i, i + STOREFRONT_NODES_MAX_IDS));
                }
                void Promise.all(chunks.map(fetchCollectionChunk)).then(() => resolve());
            });
        });
    }
    return pendingCollectionFlush;
}
// Card thumbnail — covers 2× DPR for the ~450px CSS card; WebP for size.
const SEARCH_RESULT_THUMB_TRANSFORM = `transform: { maxWidth: 1024, preferredContentType: WEBP }`;
const searchArticlesQuery = /* GraphQL */ `
	query SearchArticles($query: String!, $first: Int!, $after: String) {
		search(query: $query, types: [ARTICLE, PAGE], first: $first, after: $after) {
			edges {
				node {
					__typename
					... on Article {
						id
						title
						handle
						onlineStoreUrl
						excerpt
						image {
							url(${SEARCH_RESULT_THUMB_TRANSFORM})
							altText
						}
						blog {
							title
							handle
						}
						authorV2 {
							name
						}
						publishedAt
						tags
					}
					... on Page {
						id
						title
						handle
						onlineStoreUrl
						seo {
							description
						}
						publishedAt: createdAt
						# Pages don't have a native image field; Birdsnest stores the card
						# thumbnail in a custom.featured_image metafield (file_reference
						# pointing at a MediaImage). Falls back gracefully when unset.
						featuredImage: metafield(namespace: "custom", key: "featured_image") {
							reference {
								... on MediaImage {
									image {
										url(${SEARCH_RESULT_THUMB_TRANSFORM})
										altText
									}
								}
							}
						}
					}
				}
			}
			pageInfo {
				hasNextPage
				hasPreviousPage
				startCursor
				endCursor
			}
			totalCount
		}
	}
`;
/**
 * Searches for articles via the Shopify Storefront API search query.
 * Supports cursor-based pagination via first/after. Returns raw Storefront API response.
 */
export const searchArticles = (query, options) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    if (!(query === null || query === void 0 ? void 0 : query.trim())) {
        return { data: null, errors: null };
    }
    const first = (_a = options === null || options === void 0 ? void 0 : options.first) !== null && _a !== void 0 ? _a : 10;
    const after = (_b = options === null || options === void 0 ? void 0 : options.after) !== null && _b !== void 0 ? _b : undefined;
    try {
        const { data, errors } = yield shopify.request(searchArticlesQuery, {
            variables: Object.assign({ query, first }, (after != null && after !== '' && { after })),
        });
        return { data, errors };
    }
    catch (error) {
        console.error('[searchArticles]', error);
        return { data: null, errors: [error] };
    }
});
/**
 * Fetches Birdsnest TV data for the given product IDs via Storefront API.
 * Returns for each product:
 * - models_featured (Model metaobject): model_size from the referenced Model metaobject
 * - related_products (product references): title, handle, productFrontImage (custom.product_front_media image) for each related product
 */
export const getBirdsnestTVDataQuery = (productIds) => __awaiter(void 0, void 0, void 0, function* () {
    if (!(productIds === null || productIds === void 0 ? void 0 : productIds.length)) {
        return { data: { nodes: [] }, errors: null };
    }
    const ids = productIds.map((id) => id.startsWith("gid://") ? id : `gid://shopify/Product/${id}`);
    try {
        const { data, errors } = yield shopify.request(getBirdsnestTVData, {
            variables: { ids },
        });
        return { data, errors };
    }
    catch (error) {
        console.error("[getBirdsnestTVDataQuery]", error);
        return { data: null, errors: [error] };
    }
});
export default getProductInfoQuery;
